import type { OrderStatus } from "@/types/tms";
// Status progression rank — higher means further along. Canonical copy lives
// in `order-status.ts` so the ranking table isn't duplicated across src/lib.
import { STATUS_RANK } from "@/lib/order-status";


/**
 * Maps EAPI trip status values to internal order statuses.
 * Returns null when the EAPI status should not trigger a local change.
 *
 * NOTE on `cancelled`: this mapper is shared between READ paths
 * (display-only — `orders-with-legs.ts`, `OrderDetail.tsx`) and the
 * client-side WRITE sync (`useEapiStatusSync`). Cancellation of the
 * local `orders.status` must ONLY come from explicit user-initiated
 * cancel flows (`useOrderCancellation`, `orders/index.ts` TaaS cancel)
 * — never auto-propagated from EAPI. The mapper still returns
 * `'canceled'` so read paths can render leg state, but
 * `isForwardTransition` below explicitly rejects `canceled` as a write
 * target so the sync hook can never silently cancel an order.
 * Background: trip_type → Manual cancels the upstream EAPI trip; a
 * stale React Query cache would then race the refetch and have the
 * sync hook write `status='canceled'` locally. See
 * order `1020015b-6f67-49c2-a9ca-8e3fe27c1b56`.
 */
export function mapEapiStatusToOrderStatus(eapiStatus: string): OrderStatus | null {
  switch (eapiStatus) {
    case "enroute":
    case "in_transit":
      return "in_transit";
    case "landed":
    case "completed":
    case "delivered":
      return "delivered";
    case "cancelled":
    case "canceled":
      return "canceled";
    case "planned":
    case "preparing":
    case "confirmed":
      return "confirmed";
    case "tender_received":
      return "tender_received";
    case "unplanned":
    case "booked":
      return "booked";
    default:
      return null; // unknown — no change
  }
}





/**
 * Returns true when transitioning from `current` to `next` is a valid forward move.
 * Prevents backwards transitions and no-ops.
 */
export function isForwardTransition(current: OrderStatus, next: OrderStatus): boolean {
  const currentRank = STATUS_RANK[current] ?? -1;
  const nextRank = STATUS_RANK[next] ?? -1;
  // Don't transition away from terminal states
  if (current === "delivered" || current === "canceled") return false;
  // Cancellation is a user-initiated action only — never let an EAPI-driven
  // sync write `canceled` to a local order. See the file-level note above.
  if (next === "canceled") return false;
  return nextRank > currentRank;
}
